// Scenario draft: the editable "to-be" staffing derived from the as-is RACI
// analysis. Pure state + arithmetic, so the editor components stay presentational
// and this stays unit-testable.
//
// Effort and cost follow the same formulas the backend uses, verified against
// the payload:
//   minutes = baseMinutes * allocationPercent / 100
//   cost    = minutes / 60 * hourlyRate
// so edits recompute locally without a round trip.

import {
  ELIMINATED_CLASS,
  RACI_ROLES,
  TIME_COMPONENTS,
  UNCLASSIFIED,
} from "./whatIfNormalize";

const MINUTES_PER_HOUR = 60;

export const SCENARIO_ACTIONS = {
  setJob: "setJob",
  setJobRate: "setJobRate",
  setAllocation: "setAllocation",
  addSlot: "addSlot",
  removeSlot: "removeSlot",
  setTaskTime: "setTaskTime",
  setClassification: "setClassification",
  resetTask: "resetTask",
  resetAll: "resetAll",
};

export { ELIMINATED_CLASS };

export const isTaskEliminated = (task) =>
  task.classification === ELIMINATED_CLASS;

// RACI allows exactly one Accountable per task, and the payload models `A` as a
// single object rather than an array — so that role is capped at one assignment
// while the others are open-ended.
export const ROLE_SLOT_LIMIT = { R: Infinity, A: 1, C: Infinity, I: Infinity };

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function countRole(slots, role) {
  return slots.filter((slot) => slot.role === role).length;
}

export function canAddRole(slots, role) {
  return countRole(slots, role) < ROLE_SLOT_LIMIT[role];
}

const toOriginalSlot = (participant, id, role) => ({
  id,
  role,
  jobId: participant.jobId,
  jobName: participant.jobName,
  hourlyRateUsd: participant.hourlyRateUsd,
  allocationPercent: participant.allocationPercent ?? 0,
  minutes: participant.minutes,
  costUsd: participant.costUsd,
  // Carried for the Queuing tab only; nothing in the cost or effort arithmetic
  // reads it.
  staffCount: participant.staffCount,
});

// Slots carry their own identity rather than deriving it from position, so a
// slot survives its neighbours being added or removed.
const toEditableSlot = (original) => ({
  id: original.id,
  role: original.role,
  jobId: original.jobId,
  jobName: original.jobName,
  hourlyRateUsd: original.hourlyRateUsd,
  allocationPercent: original.allocationPercent,
  staffCount: original.staffCount,
  original,
});

export const sumTimes = (times) =>
  TIME_COMPONENTS.reduce((total, { key }) => total + toNumber(times?.[key]), 0);

export function createScenarioDraft(tasks) {
  let sequence = 0;

  const draftTasks = tasks.map((task) => {
    const originalSlots = RACI_ROLES.flatMap((role) =>
      task.participantsByRole[role].map((participant) =>
        toOriginalSlot(participant, `slot-${sequence++}`, role),
      ),
    );

    // A task the payload already reports as NVA was deducted by the backend
    // before it got here, so it contributes nothing to the as-is side either.
    // Baselining it at its full cost would open the editor already showing a
    // saving nobody made in this session.
    const startsEliminated = task.classification === ELIMINATED_CLASS;
    const baseline = (pick) =>
      startsEliminated ? 0 : originalSlots.reduce((sum, s) => sum + pick(s), 0);

    // Unlike `baseline`, this never zeroes for a task the backend had already
    // eliminated — it is the true pre-elimination figure, for the aggregate
    // "original staffing cost" shown before any edit. `originalCostUsd` has to
    // keep the zeroing: it drives per-task change badges (JobAllocationTab),
    // where crediting a backend elimination as something the user just did
    // would be wrong.
    const originalGrossCostUsd = originalSlots.reduce(
      (sum, s) => sum + s.costUsd,
      0,
    );

    return {
      taskId: task.taskId,
      taskName: task.taskName,
      order: task.order,
      baseMinutes: task.baseMinutes,
      // Durations and classification are the task tab's editable state; the
      // originals stay alongside so a reset — and the as-is column — never has
      // to re-derive them from the payload.
      times: { ...task.times },
      originalTimes: { ...task.times },
      classification: task.classification,
      originalClassification: task.classification,
      startsEliminated,
      // Baselines are summed from the very rows the editor renders, not from the
      // task's reported total. The two can disagree by a cent — the payload
      // rounds each participant cost and the task total independently — and any
      // gap would surface as a delta before the user edits anything.
      originalCostUsd: baseline((s) => s.costUsd),
      originalMinutes: baseline((s) => s.minutes),
      originalGrossCostUsd,
      // Reassignment candidates travel with the task, keyed by role: the API
      // scopes eligibility per RACI role, not per task.
      eligibleJobsByRole: task.eligibleJobsByRole,
      originalSlots,
      slots: originalSlots.map(toEditableSlot),
    };
  });

  return { tasks: draftTasks, nextSlotId: sequence };
}

const mapTask = (state, taskId, updateTask) => ({
  ...state,
  tasks: state.tasks.map((task) =>
    task.taskId === taskId ? updateTask(task) : task,
  ),
});

const mapSlot = (state, taskId, slotId, update) =>
  mapTask(state, taskId, (task) => ({
    ...task,
    slots: task.slots.map((slot) =>
      slot.id === slotId ? { ...slot, ...update } : slot,
    ),
  }));

const restoreSlots = (task) => ({
  ...task,
  slots: task.originalSlots.map(toEditableSlot),
  times: { ...task.originalTimes },
  classification: task.originalClassification,
});

export function scenarioReducer(state, action) {
  switch (action.type) {
    case SCENARIO_ACTIONS.setJob:
      return mapSlot(state, action.taskId, action.slotId, {
        jobId: action.job.jobId,
        jobName: action.job.jobName,
        hourlyRateUsd: action.job.hourlyRateUsd,
        // Headcount belongs to the job, not the slot: leaving the outgoing
        // holder's behind would have the Queuing tab size the new job's queue
        // against the wrong number of servers.
        staffCount: action.job.staffCount ?? 1,
      });

    // A job picked from the company list may have no rate to hand; it arrives
    // from GET /job/{id} a moment later. Applied as its own action so the
    // selection itself stays instant and the row is never blocked on a request.
    // Guarded by job id: by the time it lands the user may have moved on, and
    // stamping a stale rate onto whatever now holds the slot would be worse than
    // dropping it.
    case SCENARIO_ACTIONS.setJobRate:
      return mapTask(state, action.taskId, (task) => ({
        ...task,
        slots: task.slots.map((slot) =>
          slot.id === action.slotId &&
          String(slot.jobId) === String(action.jobId)
            ? {
                ...slot,
                hourlyRateUsd: action.hourlyRateUsd,
                staffCount: action.staffCount ?? slot.staffCount ?? 1,
              }
            : slot,
        ),
      }));

    case SCENARIO_ACTIONS.setAllocation:
      return mapSlot(state, action.taskId, action.slotId, {
        allocationPercent: action.allocationPercent,
      });

    case SCENARIO_ACTIONS.addSlot: {
      const task = state.tasks.find((item) => item.taskId === action.taskId);
      if (!task || !canAddRole(task.slots, action.role)) return state;

      // `original: null` marks the slot as added, which is what distinguishes it
      // from an edited one when counting changes.
      const slot = {
        id: `slot-${state.nextSlotId}`,
        role: action.role,
        jobId: null,
        jobName: "",
        hourlyRateUsd: null,
        allocationPercent: 0,
        original: null,
      };

      return {
        ...mapTask(state, action.taskId, (item) => ({
          ...item,
          slots: [...item.slots, slot],
        })),
        nextSlotId: state.nextSlotId + 1,
      };
    }

    case SCENARIO_ACTIONS.removeSlot:
      return mapTask(state, action.taskId, (task) => ({
        ...task,
        // Only assignments added in this scenario can be dropped. The ones the
        // analysis reported are existing staffing: they may be reassigned to a
        // different job or re-weighted, but never deleted.
        slots: task.slots.filter(
          (slot) => slot.id !== action.slotId || !isSlotAdded(slot),
        ),
      }));

    case SCENARIO_ACTIONS.setTaskTime:
      return mapTask(state, action.taskId, (task) => ({
        ...task,
        times: { ...task.times, [action.component]: action.minutes },
      }));

    case SCENARIO_ACTIONS.setClassification:
      return mapTask(state, action.taskId, (task) => ({
        ...task,
        // Re-picking the active class returns the task to unclassified, which
        // the picker shows as none of the three selected.
        classification:
          task.classification === action.classification
            ? UNCLASSIFIED
            : action.classification,
      }));

    case SCENARIO_ACTIONS.resetTask:
      return mapTask(state, action.taskId, restoreSlots);

    case SCENARIO_ACTIONS.resetAll:
      return { ...state, tasks: state.tasks.map(restoreSlots) };

    default:
      return state;
  }
}

export function isSlotAdded(slot) {
  return slot.original == null;
}

export function isSlotChanged(slot) {
  if (isSlotAdded(slot)) return true;
  return (
    String(slot.jobId) !== String(slot.original.jobId) ||
    toNumber(slot.allocationPercent) !==
      toNumber(slot.original.allocationPercent)
  );
}

// A missing rate must stay distinguishable from a rate of zero: some jobs
// genuinely cost nothing (an Applicant, for instance), while others simply have
// no rate on record. `Number(null)` is 0, so an emptiness check has to come
// before the numeric one — otherwise an unpriced job is silently costed as free.
const readRate = (value) => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// How far the task's processing time has been moved from what the payload
// reported. Everyone staffed on a task is working *through* that task, so
// doubling how long it takes doubles the effort attributed to them and the cost
// that follows — `baseMinutes` and the processing time are the same quantity
// (see normalizeTimes), and an edit to one has to move the other.
//
// It is expressed as a ratio rather than read straight from `times.processing`
// because the two can disagree at load: the payload may state a processing time
// that differs from the base recovered from the allocations, and the reported
// costs follow the recovered base. Scaling keeps an untouched task exactly on
// the API's figures while still responding proportionally to an edit.
//
// Waiting and rework are deliberately excluded. They lengthen the case without
// anyone working on it, so they move cycle time only — not labour or cost.
export function processingScale(task) {
  const reference = toNumber(task?.originalTimes?.processing);
  // Nothing to take a proportion of: leave the staffing untouched.
  if (reference <= 0) return 1;
  return toNumber(task?.times?.processing) / reference;
}

export function computeSlot(slot, baseMinutes, scale = 1) {
  const rate = readRate(slot.hourlyRateUsd);

  // An untouched row keeps the figures the API reported for it. Recomputing them
  // would drift by a cent or so — the payload rounds each cost to 2dp while the
  // rates behind them are not rounded — which would show a delta against the
  // as-is totals before the user has changed anything. Scaling those reported
  // figures preserves that fidelity at scale 1 while still tracking a duration
  // edit, which recomputing from the base would not.
  if (!isSlotChanged(slot)) {
    return {
      minutes: slot.original.minutes * scale,
      costUsd: slot.original.costUsd * scale,
      rateKnown: rate !== null,
      unassigned: false,
    };
  }

  const minutes =
    (toNumber(baseMinutes) * scale * toNumber(slot.allocationPercent)) / 100;

  return {
    minutes,
    rateKnown: rate !== null,
    unassigned: slot.jobId == null || slot.jobId === "",
    costUsd: rate === null ? 0 : (minutes / MINUTES_PER_HOUR) * rate,
  };
}

export function isTaskTimeChanged(task) {
  return TIME_COMPONENTS.some(
    ({ key }) => toNumber(task.times?.[key]) !== toNumber(task.originalTimes?.[key]),
  );
}

export function isTaskClassChanged(task) {
  return task.classification !== task.originalClassification;
}

export function computeTask(task) {
  const scale = processingScale(task);

  const slots = task.slots.map((slot) => ({
    slot,
    changed: isSlotChanged(slot),
    added: isSlotAdded(slot),
    ...computeSlot(slot, task.baseMinutes, scale),
  }));

  const count = (predicate) => slots.filter(predicate).length;
  const eliminated = isTaskEliminated(task);
  const staffedMinutes = slots.reduce((sum, entry) => sum + entry.minutes, 0);
  const staffedCostUsd = slots.reduce((sum, entry) => sum + entry.costUsd, 0);
  const timeChanged = isTaskTimeChanged(task);
  const classChanged = isTaskClassChanged(task);

  return {
    slots,
    // An eliminated task contributes nothing: no elapsed time, no attributed
    // effort, no labour cost. The pre-elimination figures stay available so the
    // editor can show what dropping it actually saves.
    eliminated,
    timeChanged,
    classChanged,
    // The base each allocation is a percentage of, after any duration edit —
    // what the Job Allocation tab must quote, since the stored `baseMinutes` is
    // frozen at load.
    baseMinutes: toNumber(task.baseMinutes) * scale,
    minutes: eliminated ? 0 : staffedMinutes,
    costUsd: eliminated ? 0 : staffedCostUsd,
    staffedMinutes,
    staffedCostUsd,
    taskMinutes: eliminated ? 0 : sumTimes(task.times),
    editedTaskMinutes: sumTimes(task.times),
    // Zero for a task the backend had already eliminated, matching the cost
    // baseline, so a task that arrives NVA opens at no delta on either axis.
    originalTaskMinutes: task.startsEliminated
      ? 0
      : sumTimes(task.originalTimes),
    changedCount:
      count((entry) => entry.changed) +
      (timeChanged ? 1 : 0) +
      (classChanged ? 1 : 0),
    unratedCount: eliminated ? 0 : count((entry) => !entry.rateKnown),
    unassignedCount: eliminated ? 0 : count((entry) => entry.unassigned),
  };
}

export function computeScenario(state) {
  const tasks = state.tasks.map((task) => ({ task, ...computeTask(task) }));
  const sum = (pick) => tasks.reduce((total, entry) => total + pick(entry), 0);

  return {
    tasks,
    minutes: sum((entry) => entry.minutes),
    costUsd: sum((entry) => entry.costUsd),
    originalMinutes: sum((entry) => entry.task.originalMinutes),
    originalCostUsd: sum((entry) => entry.task.originalCostUsd),
    originalGrossCostUsd: sum((entry) => entry.task.originalGrossCostUsd),
    taskMinutes: sum((entry) => entry.taskMinutes),
    originalTaskMinutes: sum((entry) => entry.originalTaskMinutes),
    eliminatedCount: tasks.filter((entry) => entry.eliminated).length,
    changedCount: sum((entry) => entry.changedCount),
    unratedCount: sum((entry) => entry.unratedCount),
    unassignedCount: sum((entry) => entry.unassignedCount),
  };
}
